import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Coins, DollarSign, Loader2, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const USD_QUICK = [1, 5, 10, 20];
const COIN_QUICK = [50, 100, 250, 500];

/**
 * PhotoTipDialog — tip the creator of a photo/creation with either Coins
 * (instant in-app transfer) or real USD (Stripe → recipient's cash-out wallet).
 */
export default function PhotoTipDialog({ open, onOpenChange, targetUsername, creationId, onTipped }) {
  const { user, refresh } = useAuth();
  // Real USD is the default — the creator receives money straight into their cash-out wallet.
  const [mode, setMode] = useState('usd'); // 'usd' | 'coins'
  const [amount, setAmount] = useState(5);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const close = () => { onOpenChange(false); setMessage(''); setAmount(mode === 'coins' ? 50 : 5); };

  const switchMode = (m) => {
    setMode(m);
    setAmount(m === 'coins' ? 50 : 5);
  };

  const send = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter an amount'); return; }
    setLoading(true);
    try {
      if (mode === 'coins') {
        await api.post('/tips/coins', {
          to_username: targetUsername,
          amount: Math.floor(amt),
          message,
          creation_id: creationId,
        });
        toast.success(`Tip Sent Successfully! 🎁 ${Math.floor(amt)} Coins → @${targetUsername}`);
        refresh();
        onTipped?.();
        close();
      } else {
        if (amt < 1) { toast.error('Minimum USD tip is $1'); return; }
        const origin = window.location.origin;
        const { data } = await api.post('/tips/usd/checkout', {
          to_username: targetUsername,
          amount_usd: amt,
          message,
          creation_id: creationId,
          origin_url: origin,
        });
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tip failed');
    } finally {
      setLoading(false);
    }
  };

  const quickValues = mode === 'coins' ? COIN_QUICK : USD_QUICK;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="glass border-white/10 max-w-sm" data-testid="photo-tip-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" /> Tip @{targetUsername}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Send Coins instantly or send real USD via secure Stripe checkout.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="tip-mode-usd"
            onClick={() => switchMode('usd')}
            className={`p-2.5 rounded-xl border flex items-center justify-center gap-2 text-sm font-bold transition-colors
              ${mode === 'usd' ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-200 shadow-lg shadow-emerald-500/10' : 'border-white/10 text-slate-400 hover:border-white/30'}`}
          >
            <DollarSign className="w-4 h-4" /> Real $$ (USD)
          </button>
          <button
            data-testid="tip-mode-coins"
            onClick={() => switchMode('coins')}
            className={`p-2.5 rounded-xl border flex items-center justify-center gap-2 text-sm font-bold transition-colors
              ${mode === 'coins' ? 'bg-amber-500/20 border-amber-400/60 text-amber-200' : 'border-white/10 text-slate-400 hover:border-white/30'}`}
          >
            <Coins className="w-4 h-4" /> Coins
          </button>
        </div>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
              Amount ({mode === 'coins' ? 'Coins' : 'USD'})
            </label>
            <Input
              data-testid="tip-amount"
              type="number"
              min={mode === 'coins' ? 1 : 1}
              step={mode === 'coins' ? 1 : 0.5}
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {quickValues.map(v => (
                <button
                  key={v}
                  data-testid={`tip-quick-${v}`}
                  onClick={() => setAmount(v)}
                  className={`text-[11px] px-3 py-1.5 rounded-full font-bold border transition-colors
                    ${+amount === v
                      ? (mode === 'coins' ? 'bg-amber-500/30 border-amber-400 text-amber-100' : 'bg-emerald-500/30 border-emerald-400 text-emerald-100')
                      : 'bg-white/5 hover:bg-white/10 border-white/10 text-slate-300'}`}
                >
                  {mode === 'coins' ? v : `$${v}`}
                </button>
              ))}
              <button
                data-testid="tip-quick-custom"
                onClick={() => setAmount('')}
                className="text-[11px] px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 font-bold"
              >
                Custom
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Message (optional)</label>
            <Textarea
              data-testid="tip-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={2}
              placeholder="Love this!"
              maxLength={200}
            />
          </div>
          <div className="text-xs text-slate-400">
            {mode === 'coins' ? (
              <>Your Coins: <span className="font-bold text-amber-300">{(user?.coins_balance || 0).toLocaleString()}</span></>
            ) : (
              <>USD tips go to the creator's cash-out wallet via Stripe (10% platform fee).</>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={loading}>Cancel</Button>
          <Button
            data-testid="tip-send"
            onClick={send}
            disabled={loading}
            className={mode === 'coins' ? 'gradient-gold text-slate-950 font-bold' : 'gradient-brand text-white font-bold'}
          >
            {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</>
              : (mode === 'coins' ? `Send ${amount || 0} Coins` : `Send $${amount || 0}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
